import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Button, Offcanvas, ProgressBar, Spinner } from 'react-bootstrap';
import IconAlert from '@/components/common/IconAlert';
import SocketProgressBar from '@/app/common/components/SocketProgressBar';
import type { CentrifugeProgressMessage } from '@/hooks/useCentrifugeProgress';
import { getIndexerErrorInfo } from '../api/errors';
import { formatIndexerErrorLine } from '../lib/format-error-line';
import {
  useLazyGetInspectJobDiffQuery,
  useLazyGetJobRetryUrlsQuery,
} from '../api/indexerApi';
import type { InspectJobDiffData, ProjectJob } from '../api/types';
import { formatTime } from '../lib/datetime';
import { canRetryFailedJob } from '../lib/job-retry';
import { formatFaNumber } from '../lib/utils';
import { toResultRow } from '../lib/result-detail';
import {
  canStopJob,
  isJobDone,
  jobStateLabel,
  jobTypeLabel,
} from '../lib/job-labels';
import type { BulkNotify } from './InspectBulkMenu';
import { IndexResultsBulkMenu, isIndexJobKind } from './IndexBulkMenu';
import { InspectJobDiffTable } from './InspectJobDiffTable';
import { InspectResultTable } from './InspectResultTable';
import { ResultTable } from './ResultTable';
import { IndexerIcon } from './IndexerIcon';
import { IndexerTablePreload } from './preloads/IndexerPreloads';

function jobErrorSummary(job: ProjectJob): string | null {
  const metaError = job.meta?.error;
  if (metaError && typeof metaError === 'object') {
    const err = metaError as {
      httpStatus?: unknown;
      message?: unknown;
      status?: unknown;
    };
    return formatIndexerErrorLine({
      httpStatus:
        typeof err.httpStatus === 'number' ? err.httpStatus : undefined,
      message: typeof err.message === 'string' ? err.message : undefined,
    });
  }

  const failed = (job.results ?? []).filter((row) => !row.success);
  const sample = failed[0];
  if (!sample) return null;

  return formatIndexerErrorLine({
    httpStatus: sample.httpStatus,
    statusLabel: sample.statusLabel,
    message: sample.detail,
  });
}

function jobResultsSubtitle(job: ProjectJob): string {
  const typeLabel = jobTypeLabel(job.type);
  const parts = [
    jobStateLabel(job.state),
    typeLabel || null,
    `${job.successCount} آدرس موفق`,
    `${job.failedCount} آدرس ناموفق`,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function JobResultsPanel({
  show,
  projectId,
  job,
  loading,
  onClose,
  onNotify,
  onRetry,
  retryBusy = false,
  onStop,
  stopBusy = false,
  onLiveRefresh,
  onLiveProgress,
}: {
  show: boolean;
  projectId: string;
  job: ProjectJob | null;
  loading: boolean;
  onClose: () => void;
  onNotify?: BulkNotify;
  onRetry?: (job: ProjectJob) => void;
  retryBusy?: boolean;
  onStop?: (job: ProjectJob) => void;
  stopBusy?: boolean;
  /** Refresh job after Centrifugo complete (final state / notIndexedUrls). */
  onLiveRefresh?: () => void;
  /** Append each finished URL row from Centrifugo without full GET. */
  onLiveProgress?: (data: CentrifugeProgressMessage) => void;
}) {
  const [showDiff, setShowDiff] = useState(false);
  const [diff, setDiff] = useState<InspectJobDiffData | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [retryUrlsLeft, setRetryUrlsLeft] = useState<number | null>(null);
  const [retryCoveredNote, setRetryCoveredNote] = useState<string | null>(null);
  const [getJobRetryUrls] = useLazyGetJobRetryUrlsQuery();
  const [getInspectJobDiff] = useLazyGetInspectJobDiffQuery();

  const processed = (job?.successCount ?? 0) + (job?.failedCount ?? 0);
  const total = job?.total ?? 0;
  const inProgress = job ? !isJobDone(job.state) : false;
  const useSocketProgress = Boolean(
    job &&
      inProgress &&
      (job.kind === 'inspect' || job.kind === 'google_index'),
  );
  const errorSummary =
    job && (job.state === 'failed' || job.state === 'partial')
      ? jobErrorSummary(job)
      : null;
  const rows = toResultRowList(job);
  const canDiff = job?.kind === 'inspect' && isJobDone(job.state);
  const maybeRetry = Boolean(job && onRetry && canRetryFailedJob(job));
  const showRetry =
    maybeRetry && (retryUrlsLeft === null || retryUrlsLeft > 0);
  const showStop = job && onStop && canStopJob(job);
  const hasRetryHandler = Boolean(onRetry);

  useEffect(() => {
    setShowDiff(false);
    setDiff(null);
    setDiffError(null);
    setRetryUrlsLeft(null);
    setRetryCoveredNote(null);
  }, [job?.id]);

  useEffect(() => {
    if (!show) {
      setShowDiff(false);
    }
  }, [show]);

  useEffect(() => {
    if (!job || !hasRetryHandler || !canRetryFailedJob(job)) {
      setRetryUrlsLeft(null);
      setRetryCoveredNote(null);
      return;
    }

    let cancelled = false;
    setRetryUrlsLeft(null);
    setRetryCoveredNote(null);
    void getJobRetryUrls({ projectId, jobId: job.id }, true)
      .unwrap()
      .then((plan) => {
        if (cancelled) return;
        setRetryUrlsLeft(plan.urls.length);
        if (plan.urls.length > 0) {
          setRetryCoveredNote(null);
          return;
        }
        setRetryCoveredNote(
          plan.skippedSucceededElsewhere > 0
            ? `باقی‌ماندهٔ این درخواست (${formatFaNumber(plan.skippedSucceededElsewhere)} آدرس) در تلاش‌های بعدی با موفقیت انجام شده؛ نیازی به تلاش مجدد نیست.`
            : 'آدرس باقی‌مانده یا ناموفقی برای تلاش مجدد نیست.',
        );
      })
      .catch(() => {
        if (!cancelled) {
          setRetryUrlsLeft(null);
          setRetryCoveredNote(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    job,
    job?.id,
    job?.state,
    job?.successCount,
    job?.failedCount,
    job?.total,
    hasRetryHandler,
    projectId,
    getJobRetryUrls,
  ]);

  useEffect(() => {
    if (!showDiff || !job || !canDiff) {
      setDiff(null);
      setDiffError(null);
      return;
    }

    let cancelled = false;
    setDiffLoading(true);
    setDiffError(null);
    void getInspectJobDiff({ projectId, jobId: job.id }, true)
      .unwrap()
      .then((data) => {
        if (!cancelled) setDiff(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setDiff(null);
          setDiffError(
            getIndexerErrorInfo(err).message || 'مقایسه با جاب قبلی ناموفق بود',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showDiff, job, canDiff, projectId, getInspectJobDiff]);

  const headerActions: ReactNode = (
    <>
      {showStop && job && (
        <Button
          size="sm"
          variant="warning"
          disabled={stopBusy}
          onClick={() => onStop?.(job)}
        >
          {stopBusy && (
            <Spinner
              size="sm"
              animation="border"
              className="me-1"
              style={{ width: 12, height: 12 }}
            />
          )}
          توقف
        </Button>
      )}
      {showRetry && job && (
        <Button
          size="sm"
          variant="outline-danger"
          className="fs--2 py-0 px-2 text-nowrap"
          disabled={retryBusy}
          onClick={() => onRetry?.(job)}
        >
          {retryBusy && (
            <Spinner
              size="sm"
              animation="border"
              className="me-1"
              style={{ width: 12, height: 12 }}
            />
          )}
          تلاش مجدد
        </Button>
      )}
      {canDiff && (
        <Button
          size="sm"
          variant={showDiff ? 'primary' : 'outline-primary'}
          className="fs--2"
          onClick={() => setShowDiff((v) => !v)}
        >
          {showDiff ? 'نمایش همهٔ نتایج' : 'مقایسه با جاب قبلی'}
        </Button>
      )}
    </>
  );

  const hasHeaderActions =
    Boolean(showStop && job) || Boolean(showRetry && job) || canDiff;

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="start"
      className="job-results-drawer"
    >
      <Offcanvas.Header closeButton className="bg-body-tertiary border-bottom py-2">
        <Offcanvas.Title as="div" className="fs--1 fw-semibold mb-0 lh-sm">
          <div>نتایج این درخواست</div>
          {job && (
            <div className="text-secondary fs--2 fw-normal mt-1">
              {jobResultsSubtitle(job)}
            </div>
          )}
        </Offcanvas.Title>
      </Offcanvas.Header>

      {hasHeaderActions && (
        <div className="job-results-drawer-actions d-flex flex-wrap gap-2 px-3 py-2 border-bottom bg-body-tertiary">
          {headerActions}
        </div>
      )}

      <Offcanvas.Body className="p-3">
        {loading && !job ? (
          <IndexerTablePreload rows={8} cols={4} />
        ) : (
          <div className="job-results-drawer-stack">
            {job?.state === 'cancelled' && !showDiff && (
              <Alert variant="warning" className="mb-0 flex-shrink-0">
                این درخواست متوقف شد؛ آدرس‌های باقی‌مانده اجرا نشدند.
                {showRetry
                  ? ' با «تلاش مجدد» می‌توانید ادامه بدهید.'
                  : ''}
              </Alert>
            )}
            {retryCoveredNote && !showDiff && (
              <IconAlert variant="info" className="mb-0 flex-shrink-0">
                <p className="mb-0 text-justify pe-3">{retryCoveredNote}</p>
              </IconAlert>
            )}
            {errorSummary && !showDiff && (
              <IconAlert variant="danger" className="mb-0 flex-shrink-0">
                <p className="mb-0 text-justify pe-3">{errorSummary}</p>
              </IconAlert>
            )}
            {job && useSocketProgress && (
              <div className="flex-shrink-0">
                <SocketProgressBar
                  channelId={job.id}
                  active={useSocketProgress}
                  className="mb-0"
                  description={
                    job.kind === 'inspect'
                      ? 'در حال بازرسی وضعیت ایندکس…'
                      : 'در حال ارسال درخواست ایندکس…'
                  }
                  onProgress={(data) => {
                    onLiveProgress?.(data);
                  }}
                  onComplete={(data) => {
                    if (data) onLiveProgress?.(data);
                    void onLiveRefresh?.();
                  }}
                />
              </div>
            )}
            {job && inProgress && !useSocketProgress && total > 0 && (
              <div className="flex-shrink-0">
                <ProgressBar
                  now={Math.round((processed / total) * 100)}
                  label={`${processed} / ${total}`}
                />
              </div>
            )}

            {showDiff && canDiff ? (
              <>
                {diffLoading && (
                  <div className="text-center py-4 text-secondary flex-shrink-0">
                    <Spinner
                      size="sm"
                      animation="border"
                      className="me-2"
                      style={{ width: 12, height: 12 }}
                    />
                    در حال مقایسه…
                  </div>
                )}
                {diffError && (
                  <Alert variant="warning" className="mb-0 flex-shrink-0">
                    {diffError}
                  </Alert>
                )}
                {diff && !diffLoading && (
                  <InspectJobDiffTable
                    projectId={projectId}
                    diff={diff}
                    compareToLabel={formatTime(diff.compareToCreatedAt)}
                    fillHeight
                  />
                )}
              </>
            ) : job?.kind === 'inspect' ? (
              <InspectResultTable
                projectId={projectId}
                results={job.results ?? []}
                onNotify={onNotify}
                fillHeight
              />
            ) : job && isIndexJobKind(job.kind) ? (
              <>
                {rows.length > 0 && (
                  <div className="d-flex flex-wrap justify-content-end align-items-center gap-2 flex-shrink-0">
                    <IndexResultsBulkMenu
                      results={job.results ?? []}
                      successCount={job.successCount}
                      failedCount={job.failedCount}
                      onNotify={onNotify}
                    />
                  </div>
                )}
                {(rows.length > 0 || !errorSummary) && (
                  <ResultTable rows={rows} fillHeight />
                )}
              </>
            ) : rows.length === 0 ? (
              errorSummary ? null : (
                <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary py-5">
                  <IndexerIcon name="table" />
                  <p className="mb-0">هنوز نتیجه‌ای ثبت نشده است.</p>
                </div>
              )
            ) : (
              <ResultTable rows={rows} fillHeight />
            )}
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function toResultRowList(job: ProjectJob | null) {
  return (job?.results ?? []).map(toResultRow);
}
